// CONTRACT:
// PURPOSE: [Inline 4-column breakdown of trust index components для rating company card]
// SCOPE: [rating card, breakdown, expandable]
// INPUT: [rating, reviewsCount, status, registrationDate, inn, ogrn, directorName, address, responseScore]
// OUTPUT: [4-column grid with progress bars and formal data checklist]
// KEYWORDS: [DOMAIN(SEO): index-breakdown; CONCEPT(scoring): formal-data, market-experience, reviews, response; UI: grid, progress-bar, checklist]

"use client";

import { Shield, CheckCircle, AlertCircle } from "lucide-react";
import type { Json } from "@/types/database";

// # START_INTERFACE_CompanyIndexBreakdownProps
interface V54Breakdown {
  reg: number;
  age: number;
  license: number;
  esg: number;
  reviews: number;
  financials: number;
  confirmation: number;
  total: number;
  active: boolean;
}

interface CompanyIndexBreakdownProps {
  /** Trust Index v5.4 breakdown (7 слоёв, canon docs/rating-formula-v5.md). Если задан — рендерим v5.4, иначе legacy 4-колоночную разбивку. */
  v54Breakdown?: Json | null;
  rating: number | null;
  reviewsCount: number | null;
  status: string | null;
  registrationDate: string | null;
  inn: string | null;
  ogrn: string | null;
  directorName: string | null;
  address: string | null;
  responseScore?: number | null;
}
// # END_INTERFACE_CompanyIndexBreakdownProps

// # START_FUNCTION_calculateYears
function calculateYears(registrationDate: string | null): number {
  if (!registrationDate) return 0;
  return (
    (Date.now() - new Date(registrationDate).getTime()) /
    (365.25 * 24 * 60 * 60 * 1000)
  );
}
// # END_FUNCTION_calculateYears

// # START_FUNCTION_calculateFormalScore
function calculateFormalScore(props: {
  status: string | null;
  registrationDate: string | null;
  inn: string | null;
  ogrn: string | null;
  directorName: string | null;
  address: string | null;
}): { score: number; checklist: { label: string; ok: boolean }[] } {
  const years = calculateYears(props.registrationDate);
  const checklist = [
    { label: "Статус", ok: props.status === "ACTIVE" },
    { label: "Возраст", ok: years >= 1 },
    { label: "ИНН", ok: !!props.inn },
    { label: "ОГРН", ok: !!props.ogrn },
    { label: "Руководитель", ok: !!props.directorName },
    { label: "Адрес", ok: !!props.address },
  ];
  const score = Math.round(
    (checklist.filter((d) => d.ok).length / checklist.length) * 100
  );
  return { score, checklist };
}
// # END_FUNCTION_calculateFormalScore

// # START_FUNCTION_BreakdownColumn
/**
 * Одна колонка разбивки индекса.
 * Показывает название, вес, значение и прогресс-бар.
 */
function BreakdownColumn({
  name,
  weight,
  value,
  maxValue = 100,
  gradientFrom = "from-green-400",
  gradientTo = "to-emerald-300",
}: {
  name: string;
  weight: number;
  value: number;
  maxValue?: number;
  gradientFrom?: string;
  gradientTo?: string;
}) {
  const percent = Math.min(100, Math.round((value / maxValue) * 100));

  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-[hsl(var(--portal-text-secondary))] font-mono uppercase tracking-wide">
          {name}
        </span>
        <span className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono whitespace-nowrap">
          вес {weight}%
        </span>
      </div>
      {/* value */}
      <div className="text-xl font-black font-mono text-[hsl(var(--portal-text))]">
        {percent}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
// # END_FUNCTION_BreakdownColumn

// # START_FUNCTION_FormalDataColumn
/**
 * Колонка «Формальные данные» — показывает formalScore + мини-чеклист ✅/❌.
 */
function FormalDataColumn({
  score,
  checklist,
}: {
  score: number;
  checklist: { label: string; ok: boolean }[];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-[hsl(var(--portal-text-secondary))] font-mono uppercase tracking-wide">
          Формальные данные
        </span>
        <span className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono whitespace-nowrap">
          вес 25%
        </span>
      </div>
      {/* value: score */}
      <div className="text-xl font-black font-mono text-[hsl(var(--portal-text))]">
        {score}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
          style={{ width: `${score}%` }}
        />
      </div>
      {/* mini-checklist 2x3 grid */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
        {checklist.map((item) => (
          <span
            key={item.label}
            className={`text-[9px] font-mono ${
              item.ok ? "text-green-400/80" : "text-[hsl(var(--portal-text-dim))]"
            }`}
          >
            {item.ok ? "✅" : "❌"} {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
// # END_FUNCTION_FormalDataColumn

// # START_FUNCTION_ResponseColumn
/**
 * Колонка «Реакция на жалобы». Если responseScore null — показывает "Скоро".
 */
function ResponseColumn({
  responseScore,
}: {
  responseScore: number | null | undefined;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-[hsl(var(--portal-text-secondary))] font-mono uppercase tracking-wide">
          Реакция на жалобы
        </span>
        <span className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono whitespace-nowrap">
          вес 20%
        </span>
      </div>
      {responseScore != null ? (
        <>
          <div className="text-xl font-black font-mono text-[hsl(var(--portal-text))]">
            {Math.min(100, Math.round(responseScore))}
          </div>
          <div className="h-1 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
              style={{ width: `${Math.min(100, responseScore)}%` }}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5 mb-1">
            <AlertCircle className="w-3 h-3 text-[hsl(var(--portal-text-dim))]" />
            <span className="text-[10px] text-[hsl(var(--portal-text-dim))] font-mono italic">
              Скоро
            </span>
          </div>
          <div className="h-1 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300" style={{ width: "0%" }} />
          </div>
        </>
      )}
    </div>
  );
}
// # END_FUNCTION_ResponseColumn

// # START_FUNCTION_MarketExperienceColumn
/**
 * Колонка «Опыт на рынке» — рассчитывается из registration_date.
 * min(100, years * 7) — 14+ лет = полный балл.
 */
function MarketExperienceColumn({
  registrationDate,
}: {
  registrationDate: string | null;
}) {
  const years = calculateYears(registrationDate);
  const value = Math.min(100, Math.round(years * 7));

  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-[hsl(var(--portal-text-secondary))] font-mono uppercase tracking-wide">
          Опыт на рынке
        </span>
        <span className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono whitespace-nowrap">
          вес 20%
        </span>
      </div>
      {/* value */}
      <div className="text-xl font-black font-mono text-[hsl(var(--portal-text))]">
        {value}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
          style={{ width: `${value}%` }}
        />
      </div>
      {/* years hint */}
      <span className="text-[9px] text-[hsl(var(--portal-text-dim))] font-mono">
        {years > 0 ? `${Math.round(years)} лет на рынке` : "Нет данных"}
      </span>
    </div>
  );
}
// # END_FUNCTION_MarketExperienceColumn

// # START_FUNCTION_CompanyIndexBreakdown
/**
 * Компактная 4-колоночная сетка разбивки индекса.
 * Inline — без заголовка и общего балла (они в карточке).
 * Desktop: 4 колонки, Mobile: 2 колонки.
 */
export function CompanyIndexBreakdown(props: CompanyIndexBreakdownProps) {
  // MEMENTO: v5.4 breakdown (trust_breakdown_v54 из миграции 20260824000001) — 7 слоёв по 100-балльной шкале.
  // Если v54 доступен — показываем его, иначе legacy разбивку (старый trust_score).
  if (props.v54Breakdown && typeof props.v54Breakdown === "object" && !Array.isArray(props.v54Breakdown)) {
    const b = props.v54Breakdown as unknown as V54Breakdown;
    const layers: { name: string; weight: number; value: number }[] = [
      { name: "Регистрация", weight: 20, value: b.reg },
      { name: "Возраст", weight: 20, value: b.age },
      { name: "Лицензия", weight: 10, value: b.license },
      { name: "ESG", weight: 10, value: b.esg },
      { name: "Отзывы", weight: 15, value: b.reviews },
      { name: "Финансы", weight: 20, value: b.financials },
      { name: "Подтверждение", weight: 5, value: b.confirmation },
    ];
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        {layers.map((l) => (
          <BreakdownColumn
            key={l.name}
            name={l.name}
            weight={l.weight}
            value={Math.min(100, Math.round((l.value / l.weight) * 100))}
          />
        ))}
      </div>
    );
  }

  const reviewsValue =
    props.rating != null && props.rating > 0
      ? Math.round((props.rating / 5) * 100)
      : 0;

  const { score: formalScore, checklist } = calculateFormalScore(props);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
      {/* 1. Отзывы заказчиков — вес 35% */}
      <BreakdownColumn
        name="Отзывы заказчиков"
        weight={35}
        value={reviewsValue}
      />

      {/* 2. Формальные данные — вес 25% (с чеклистом) */}
      <FormalDataColumn score={formalScore} checklist={checklist} />

      {/* 3. Опыт на рынке — вес 20% */}
      <MarketExperienceColumn registrationDate={props.registrationDate} />

      {/* 4. Реакция на жалобы — вес 20% */}
      <ResponseColumn responseScore={props.responseScore} />
    </div>
  );
}
// # END_FUNCTION_CompanyIndexBreakdown
